/**
 * Login = email + OTP ONLY (feature #3) — passwordless, and FREE (no SMS).
 *
 * The code is emailed by the Apps Script endpoint (see lib/otp/emailOtpProvider).
 * Routing by status happens AFTER sign-in, driven by the `users/{uid}` doc:
 *   - no doc            → Registration (post-OTP)
 *   - pending           → under-review
 *   - approved          → Home
 *   - blocked/rejected  → blocked
 * (handled by SplashScreen/Guards reacting to AuthContext).
 *
 * Step 1 (email): normalize/validate → send code → step 2.
 * Step 2 (otp): verify code via the OTP provider abstraction. 60s resend
 *   cooldown (OTP_RESEND_COOLDOWN_SEC). On success, AuthContext + guards route
 *   onward via the Splash screen.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EMAIL_OTP_CODE_LENGTH, OTP_RESEND_COOLDOWN_SEC } from '@mitang/shared';
import { CenteredScreen } from '../../components/Screen';
import { otpProvider, isValidEmail, type OtpConfirmation } from '../../lib/otp';
import { getLastEmail, setLastEmail } from '../../lib/prefs';
import { ROUTES } from '../../routes/paths';

type Step = 'email' | 'otp';

export function LoginScreen(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [emailRaw, setEmailRaw] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const confirmationRef = useRef<OtpConfirmation | null>(null);

  // Prefill the last-used email (stored on successful sends).
  useEffect(() => {
    void getLastEmail().then((last) => {
      if (last) setEmailRaw((current) => current || last);
    });
  }, []);

  // Resend cooldown ticker.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendOtp = useCallback(async (target: string) => {
    const confirmation = await otpProvider.sendOtp(target);
    confirmationRef.current = confirmation;
    setCooldown(OTP_RESEND_COOLDOWN_SEC);
  }, []);

  const onSubmitEmail = useCallback(async () => {
    setError(null);
    const normalized = emailRaw.trim().toLowerCase();
    if (!isValidEmail(normalized)) {
      setError('Enter a valid email address.');
      return;
    }
    setBusy(true);
    try {
      await setLastEmail(normalized);
      setEmail(normalized);
      await sendOtp(normalized);
      setStep('otp');
      setCode('');
    } catch (e) {
      setError(errMessage(e, 'Could not send the code. Please try again.'));
    } finally {
      setBusy(false);
    }
  }, [emailRaw, sendOtp]);

  const onVerify = useCallback(async () => {
    setError(null);
    const confirmation = confirmationRef.current;
    if (!confirmation) {
      setError('Please request a new code.');
      setStep('email');
      return;
    }
    if (code.trim().length < EMAIL_OTP_CODE_LENGTH) {
      setError('Enter the code you received.');
      return;
    }
    setBusy(true);
    try {
      await otpProvider.verifyOtp(confirmation, code.trim());
      // AuthContext picks up the sign-in; Splash/Guards route onward based on
      // the users/{uid} doc (none → Register, pending → under-review, etc).
      navigate(ROUTES.splash, { replace: true });
    } catch (e) {
      setError(errMessage(e, 'Invalid or expired code.'));
    } finally {
      setBusy(false);
    }
  }, [code, navigate]);

  const onResend = useCallback(async () => {
    if (cooldown > 0 || !email) return;
    setError(null);
    setBusy(true);
    try {
      await sendOtp(email);
    } catch (e) {
      setError(errMessage(e, 'Could not resend the code.'));
    } finally {
      setBusy(false);
    }
  }, [cooldown, email, sendOtp]);

  if (step === 'email') {
    return (
      <CenteredScreen
        title="Sign in"
        subtitle="Enter your email to continue. We'll send you a one-time code — no password needed."
        logo="/logo-full.png"
      >
        <label className="label" htmlFor="email">
          Email address
        </label>
        <input
          id="email"
          className="field"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={emailRaw}
          onChange={(e) => setEmailRaw(e.target.value)}
        />
        {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
        <button
          type="button"
          className="btn-primary mt-5 w-full"
          disabled={busy}
          onClick={() => void onSubmitEmail()}
        >
          {busy ? 'Please wait…' : 'Email me a code'}
        </button>
      </CenteredScreen>
    );
  }

  return (
    <CenteredScreen title="Enter code" subtitle={`We emailed a code to ${email}. Check spam if you don't see it.`}>
      <label className="label" htmlFor="otp">
        One-time code
      </label>
      <input
        id="otp"
        className="field tracking-[0.4em]"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={EMAIL_OTP_CODE_LENGTH}
        placeholder="••••••"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
      />
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
      <button
        type="button"
        className="btn-primary mt-5 w-full"
        disabled={busy}
        onClick={() => void onVerify()}
      >
        {busy ? 'Verifying…' : 'Verify & sign in'}
      </button>
      <button
        type="button"
        className="btn-secondary mt-3 w-full"
        disabled={cooldown > 0 || busy}
        onClick={() => void onResend()}
      >
        {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
      </button>
      <button
        type="button"
        className="mt-3 w-full text-sm text-slate-500 underline"
        onClick={() => {
          setStep('email');
          setCode('');
          confirmationRef.current = null;
          otpProvider.reset?.();
        }}
      >
        Change email
      </button>
    </CenteredScreen>
  );
}

function errMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  return fallback;
}
